import { FaustHooks } from "@faustwp/core";
import { RetryLink } from "@apollo/client/link/retry";

/**
 * PersistedQueryRetry
 *
 * Works around a concurrency bug in wpgraphql-smart-cache's persisted-query
 * registration. During `next build`, pages are generated in parallel, so
 * multiple workers race to register the same not-yet-persisted query
 * (notably `GetPageWrapperData`, which runs on every page). The first save
 * wins; a concurrent save returns a transient HTTP 500 whose body is
 * `This query has already been associated with another query "..."`.
 * A single failed fetch aborts the entire build.
 *
 * This prepends an Apollo RetryLink that retries ONLY that specific transient
 * 500. By the time the retry fires, the winning save has completed and the
 * query resolves by its hash. Non-matching errors are not retried, so genuine
 * failures still surface immediately.
 *
 * Toggle off with `PQ_RETRY=off` to reproduce the baseline failure.
 */
const REGISTRATION_RACE = /already been associated with another query/i;

export class PersistedQueryRetry {
  apply(hooks: FaustHooks) {
    hooks.addFilter("apolloClientOptions", "rockhall", (options) => {
      const retryLink = new RetryLink({
        delay: { initial: 150, max: 2000, jitter: true },
        attempts: {
          max: 5,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          retryIf: (error: any) => {
            if (!error) return false;
            const status = error.statusCode ?? error.response?.status;
            if (status !== 500) return false;

            const messages: string[] = [];
            if (typeof error.message === "string") messages.push(error.message);
            const resultErrors = error.result?.errors;
            if (Array.isArray(resultErrors)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              resultErrors.forEach((e: any) => {
                if (e?.message) messages.push(e.message);
              });
            }

            return messages.some((m) => REGISTRATION_RACE.test(m));
          },
        },
      });

      const link = options.link ? retryLink.concat(options.link) : retryLink;
      return { ...options, link };
    });
  }
}
